<template>
  <div class="day-review-chart">
    <div class="chart-legend" ref="legendRef"></div>
    <div class="chart-canvas" ref="containerRef"></div>
    <div v-if="!candles.length" class="chart-empty text-medium-emphasis">No candle data for this day.</div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { createChart, CandlestickSeries, createSeriesMarkers } from 'lightweight-charts';

const props = defineProps({
  candles: { type: Array, default: () => [] },
  liveTrade: { type: Object, default: null },
  backtestResult: { type: Object, default: null },
});

const containerRef = ref(null);
const legendRef = ref(null);
let chart = null;
let candleSeries = null;
let markersApi = null;
const priceLines = [];

// lightweight-charts always displays in UTC; our candle_time is stored as
// IST wall-clock (process TZ is Asia/Kolkata everywhere in this app), so
// shifting by +5:30 after converting to unix seconds makes the axis read as
// IST clock time instead of the UTC instant -- same trick used by the
// reference project's chart component.
const IST_OFFSET_SECONDS = 5.5 * 3600;
function toChartTime(value) {
  return Math.floor(new Date(value).getTime() / 1000) + IST_OFFSET_SECONDS;
}

function formatPrice(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : '-';
}

function clearPriceLines() {
  for (const line of priceLines) candleSeries.removePriceLine(line);
  priceLines.length = 0;
}

function addLevel(price, color, title, lineStyle = 2) {
  if (price === null || price === undefined || !Number.isFinite(Number(price))) return;
  priceLines.push(candleSeries.createPriceLine({
    price: Number(price),
    color,
    lineWidth: 2,
    lineStyle,
    axisLabelVisible: true,
    title,
  }));
}

function buildMarkers() {
  const markers = [];
  const trade = props.liveTrade;
  if (trade?.opened_at) {
    markers.push({
      time: toChartTime(trade.opened_at),
      position: trade.direction === 'up' ? 'aboveBar' : 'belowBar',
      color: '#2563eb',
      shape: trade.direction === 'up' ? 'arrowDown' : 'arrowUp',
      size: 1,
      text: `Entry ${formatPrice(trade.entry_price)}`,
    });
  }
  if (trade?.closed_at) {
    const won = trade.status === 'won';
    markers.push({
      time: toChartTime(trade.closed_at),
      position: trade.direction === 'up' ? 'belowBar' : 'aboveBar',
      color: won ? '#0f8f68' : trade.status === 'lost' ? '#b42318' : '#b08a2e',
      shape: 'circle',
      size: 1,
      text: `${trade.exit_reason || trade.status} ${formatPrice(trade.exit_price)}`,
    });
  }
  markers.sort((a, b) => a.time - b.time);
  return markers;
}

function render() {
  if (!candleSeries) return;

  const chartCandles = props.candles.map((c) => ({
    time: toChartTime(c.timestamp),
    open: Number(c.open),
    high: Number(c.high),
    low: Number(c.low),
    close: Number(c.close),
  }));
  candleSeries.setData(chartCandles);

  clearPriceLines();

  const trade = props.liveTrade;
  const backtest = props.backtestResult;
  const entryPrice = trade?.entry_price ?? backtest?.entry_price;

  addLevel(entryPrice, '#586575', 'Entry', 0);
  if (trade) {
    addLevel(trade.stop_price, '#b42318', 'Live Stop');
    addLevel(trade.target_price, '#0f8f68', 'Live Target');
  }
  if (backtest) {
    addLevel(backtest.single_exit_price, '#2563eb', `Single Exit (${backtest.single_exit_reason})`);
    addLevel(backtest.multi_exit_price, '#9333ea', `Multi Exit (${backtest.multi_exit_reason})`);
  }

  markersApi?.setMarkers(buildMarkers());

  if (chartCandles.length) chart.timeScale().fitContent();
}

function renderLegend(candle) {
  if (!legendRef.value) return;
  if (!candle) { legendRef.value.innerHTML = ''; return; }
  legendRef.value.innerHTML =
    `<span>O <b>${formatPrice(candle.open)}</b></span>` +
    `<span>H <b>${formatPrice(candle.high)}</b></span>` +
    `<span>L <b>${formatPrice(candle.low)}</b></span>` +
    `<span>C <b>${formatPrice(candle.close)}</b></span>`;
}

onMounted(() => {
  const width = containerRef.value.clientWidth;
  chart = createChart(containerRef.value, {
    width,
    height: 420,
    layout: { background: { color: 'transparent' }, textColor: '#344054' },
    grid: { vertLines: { color: '#eef2f1' }, horzLines: { color: '#eef2f1' } },
    rightPriceScale: { borderColor: '#d7e0dd' },
    timeScale: { borderColor: '#d7e0dd', timeVisible: true, secondsVisible: false },
    crosshair: { mode: 1 },
  });

  candleSeries = chart.addSeries(CandlestickSeries, {
    upColor: '#0f8f68', downColor: '#b42318', borderVisible: false,
    wickUpColor: '#0f8f68', wickDownColor: '#b42318',
  });
  markersApi = createSeriesMarkers(candleSeries, []);

  chart.subscribeCrosshairMove((param) => {
    const point = param.seriesData?.get(candleSeries);
    renderLegend(point || null);
  });

  const resizeObserver = new ResizeObserver(() => {
    chart.applyOptions({ width: containerRef.value.clientWidth });
  });
  resizeObserver.observe(containerRef.value);

  onBeforeUnmount(() => {
    resizeObserver.disconnect();
    chart.remove();
    chart = null;
  });

  render();
});

watch(() => [props.candles, props.liveTrade, props.backtestResult], render, { deep: true });
</script>

<style scoped>
.day-review-chart {
  position: relative;
}

.chart-legend {
  display: flex;
  gap: 16px;
  font-size: 0.8rem;
  font-family: ui-monospace, monospace;
  padding: 4px 8px;
  min-height: 24px;
}

.chart-empty {
  padding: 32px;
  text-align: center;
}
</style>
